import { useEffect, useState } from 'react';
import Home from './pages/Home';
import Login from './pages/Login';
import HelpGuide from './pages/HelpGuide';
import AddSpending from './pages/AddSpending';
import Payments from './pages/Payments';
import Expenses from './pages/Expenses';
import { checkSession, type User } from './lib/authClient';
import type { FixDraft } from './lib/fixDraft';

// "Fix this payment" (AD-19): the deleted payment's draft, held only until the wizard is left.
type Fix = { draft: FixDraft; resetIngestionId: string | null };

type AppState =
  | { status: 'loading' }
  | { status: 'unauthenticated'; initialError?: string }
  | {
      status: 'authenticated';
      user: User;
      screen: 'home' | 'help' | 'add-spending' | 'payments' | 'expenses';
      fix?: Fix;
    };

export default function App() {
  const [state, setState] = useState<AppState>({ status: 'loading' });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');

    if (token) {
      // Email links are retired (AD-06, amended 2026-10-02): strip the token without
      // calling the server, and point the user to the emailed code.
      history.replaceState(null, '', '/');
      setState({
        status: 'unauthenticated',
        initialError: 'Sign-in links no longer work. Enter your email to get a sign-in code.',
      });
    } else {
      checkSession()
        .then((user) => {
          if (user) {
            setState({ status: 'authenticated', user, screen: 'home' });
          } else {
            setState({ status: 'unauthenticated' });
          }
        })
        .catch(() => setState({ status: 'unauthenticated' }));
    }
  }, []);

  if (state.status === 'loading') {
    return (
      <div
        style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        aria-busy="true"
        aria-label="Loading"
      >
        <span style={{ fontSize: '20px', color: '#666' }}>Loading…</span>
      </div>
    );
  }

  if (state.status === 'unauthenticated') {
    return (
      <Login
        onAuthSuccess={(user) => setState({ status: 'authenticated', user, screen: 'home' })}
        initialError={state.initialError}
      />
    );
  }

  if (state.screen === 'help') {
    return (
      <HelpGuide
        onBack={() => setState({ ...state, screen: 'home' })}
        onLoggedOut={() => setState({ status: 'unauthenticated' })}
      />
    );
  }

  if (state.screen === 'add-spending') {
    return <AddSpending fix={state.fix} onDone={() => setState({ ...state, screen: 'home', fix: undefined })} />;
  }

  if (state.screen === 'payments') {
    return (
      <Payments
        onDone={() => setState({ ...state, screen: 'home' })}
        onFix={(draft, resetIngestionId) => setState({ ...state, screen: 'add-spending', fix: { draft, resetIngestionId } })}
      />
    );
  }

  if (state.screen === 'expenses') {
    return (
      <Expenses
        onDone={() => setState({ ...state, screen: 'home' })}
        onFix={(draft, resetIngestionId) => setState({ ...state, screen: 'add-spending', fix: { draft, resetIngestionId } })}
      />
    );
  }

  // Add Income is a no-op; deferred to Phase 5 (income/revenue).
  return (
    <Home
      onAddSpending={() => setState({ ...state, screen: 'add-spending', fix: undefined })}
      onViewPayments={() => setState({ ...state, screen: 'payments' })}
      onViewExpenses={() => setState({ ...state, screen: 'expenses' })}
      onAddIncome={() => {}}
      onHelp={() => setState({ ...state, screen: 'help' })}
    />
  );
}
